import { useEffect, useMemo, useState } from 'react';
import { api, ApiError } from '../lib/api';
import { formatDateLatam } from '../lib/dates';

interface Missionary {
  _id?: string;
  id?: string;
  name?: string;
  email?: string;
  avatar_url?: string;
  active?: boolean;
  created_at?: string;
}

const idOf = (m: Missionary) => String(m._id ?? m.id ?? '');

export default function MissionariesPage() {
  const [missionaries, setMissionaries] = useState<Missionary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const data = await api.get<Missionary[]>('/admin/missionaries');
        if (alive) setMissionaries(Array.isArray(data) ? data : []);
      } catch (err) {
        if (alive) setError(err instanceof ApiError ? err.message : 'No se pudieron cargar los misioneros');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  const toggle = async (m: Missionary) => {
    const id = idOf(m);
    if (!id) return;
    const wasActive = m.active !== false;
    try {
      await api.put(`/admin/missionaries/${id}/status`, { active: !wasActive });
      setMissionaries(prev => prev.map(x => idOf(x) === id ? { ...x, active: !wasActive } : x));
    } catch (err) {
      alert(err instanceof ApiError ? err.message : 'No se pudo cambiar el estado');
    }
  };

  const filtered = useMemo(() => {
    if (!search.trim()) return missionaries;
    const q = search.toLowerCase();
    return missionaries.filter(m =>
      (m.name || '').toLowerCase().includes(q) ||
      (m.email || '').toLowerCase().includes(q)
    );
  }, [missionaries, search]);

  return (
    <>
      <h1 className="page-title">Misioneros ({missionaries.length})</h1>
      <div className="card" style={{ padding: 14 }}>
        <input
          className=""
          style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg)' }}
          placeholder="Buscar por nombre o email…"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
      </div>
      {loading ? <p className="muted">Cargando…</p> :
       error ? <div className="alert alert-error">{error}</div> :
       filtered.length === 0 ? (
         <div className="empty">{missionaries.length === 0 ? 'Sin misioneros aún' : 'Sin resultados para esa búsqueda'}</div>
       ) : filtered.map(m => (
         <div key={idOf(m) || m.email} className="missionary-row">
           <div className="missionary-avatar">
             {m.avatar_url
               ? <img src={m.avatar_url} alt="" />
               : (m.name || m.email || 'M').charAt(0).toUpperCase()
             }
           </div>
           <div className="missionary-meta">
             <div className="name">{m.name || 'Sin nombre'}</div>
             <div className="email">{m.email}</div>
             {m.created_at && <div className="muted" style={{ fontSize: 11 }}>Desde {formatDateLatam(m.created_at)}</div>}
           </div>
           <button
             className={'status-pill' + (m.active === false ? ' off' : '')}
             onClick={() => toggle(m)}
             title="Cambiar estado"
           >
             {m.active === false ? 'Inactivo' : 'Activo'}
           </button>
         </div>
       ))
      }
    </>
  );
}
